// models/Block.ts

import { parseRatingType, RatingType, serializeRatingType } from './RatingType';
import { sanitizeBlockHtml } from '../helpers/htmlSanitizer';

export interface Block {
  id: number;
  wodId: number;
  title: string;
  work: string;
  restText: string;
  createdAt: Date;
  updatedAt: Date;
  date?: Date;
  createdBy: number;
  updatedBy: number;
  is_deleted: number;
  capTime: string;
  goal: string;
  isTeam: number;
  blockRoot?: number;
  isBenchmark: number;
  ranking_type: string;
  ratingTypes: RatingType[];
  pictureUrl?: string;
}

export const parseBlock = (json: any): Block => ({
  id: json.id,
  wodId: json.wod_id,
  title: json.title,
  // Sanitizar HTML del campo work para eliminar tags peligrosos (h1, h2, div, span, etc.)
  // Esto previene que HTML de editores ricos (Word, Notion, Google Docs) rompa el layout
  work: sanitizeBlockHtml(json.work || ''),
  restText: json.rest_text,
  createdAt: new Date(json.created_at),
  updatedAt: new Date(json.updated_at),
  date: json.date ? new Date(json.date) : undefined,
  createdBy: json.created_by,
  updatedBy: json.updated_by,
  is_deleted: json.is_deleted,
  capTime: json.cap_time,
  goal: json.goal,
  isTeam: json.is_team,
  blockRoot: json.block_root,
  isBenchmark: json.is_benchmark,
  ranking_type: json.ranking_type,
  ratingTypes: Array.isArray(json.ratingTypes)
    ? json.ratingTypes
        .filter((rt: any) => rt.is_deleted !== 1) // <-- quita ratingTypes borrados
        .map((rt: any) => parseRatingType(rt))
    : [],
  pictureUrl: json.picture_url,
});

export const serializeBlock = (block: Block): any => ({
  id: block.id,
  wod_id: block.wodId,
  title: block.title,
  work: block.work,
  rest_text: block.restText,
  created_at: block.createdAt.toISOString(),
  updated_at: block.updatedAt.toISOString(),
  date: block.date ? block.date.toISOString() : undefined,
  created_by: block.createdBy,
  updated_by: block.updatedBy,
  is_deleted: block.is_deleted,
  cap_time: block.capTime,
  goal: block.goal,
  is_team: block.isTeam,
  block_root: block.blockRoot,
  is_benchmark: block.isBenchmark,
  ranking_type: block.ranking_type,
  ratingTypes: block.ratingTypes.map((rt) => serializeRatingType(rt)),
  picture_url: block.pictureUrl,
});

// Utility to build specs array similar to Dart `specs` getter
export type SpecItem = { title: string; value: string; icon: string };

export const getBlockSpecs = (block: Block): SpecItem[][] => {
  const specs: SpecItem[] = [];

  if (block.ratingTypes.length > 0) {
    specs.push({
      title: 'Marca: ',
      value: block.ratingTypes.map((rt) => rt.name.toUpperCase()).join(', '),
      icon: 'route_square',
    });
  }
  if (block.capTime) {
    specs.push({ title: 'Timecap: ', value: block.capTime, icon: 'clock_bolt' });
  }
  if (block.goal) {
    specs.push({ title: 'Objetivo: ', value: block.goal, icon: 'medal' });
  }
  if (block.restText) {
    specs.push({ title: 'Rest: ', value: block.restText, icon: 'clock_heart' });
  }
  if (block.isTeam === 1) {
    specs.push({ title: '', value: 'Team', icon: 'users' });
  }

  // Split into rows of max 3
  const rows: SpecItem[][] = [];
  for (let i = 0; i < specs.length; i += 3) {
    rows.push(specs.slice(i, i + 3));
  }
  return rows;
};
